// Steps inside one page that the phone's Back gesture should walk (approved 2026-10-09:
// "needs to behave like the big boy apps"). A sheet, a quiz question, a claim step.
//
// The page says how many steps deep it is. Going deeper adds a Back step. Going back by the page's
// own button removes them. The gesture runs the page's onBack, which steps back one.
// One page area uses it at a time: the admin pages, the trainee quiz, the bag tag claim, New Trainee.
// The QEW pages use nav-history.ts instead.

export interface HistoryLike {
  pushState(data: unknown, unused: string): void;
  go(delta: number): void;
}

export function stepTracker(h: HistoryLike) {
  let depth = 0;
  let skip = 0;
  let onBack: (() => void) | null = null;
  return {
    /** The page is now n steps deep. onBack steps it back one. */
    set(n: number, back: () => void): void {
      onBack = back;
      if (n > depth) {
        while (depth < n) {
          depth++;
          h.pushState({ step: depth }, "");
        }
      } else if (n < depth) {
        skip++;
        h.go(n - depth);
        depth = n;
      }
    },
    /** The page changed by address (a link): the old steps no longer apply. Nothing moves. */
    reset(): void {
      depth = 0;
      onBack = null;
    },
    /** The browser moved back. True when it was one of our steps. */
    popped(): boolean {
      if (skip) {
        skip--;
        return true;
      }
      if (!depth) return false;
      depth--;
      onBack?.();
      return true;
    },
    get depth(): number {
      return depth;
    },
  };
}

let shared: ReturnType<typeof stepTracker> | null = null;

/** The page's tracker, wired to the browser on first use. */
export function steps(): ReturnType<typeof stepTracker> {
  if (!shared) {
    shared = stepTracker(window.history);
    window.addEventListener("popstate", () => {
      shared?.popped();
    });
  }
  return shared;
}
